// View Appointment dialog (iter-268) — surfaces the full context of a
// tile in one glance so the front-desk doesn't have to open the wizard
// for read-only inspection. Triggered by a single left-click / single
// tap on any appointment tile; long-press or right-click still open
// the legacy context menu. Layout mirrors the Mindbody screenshot the
// user shared: header with print + close, service line in bold with
// duration, branch/specialist/room row, guest details block, and a
// footer with Edit (green) · Take Payment (grey, gated on check-in) ·
// Check-In (or "Checked-In" pill) · Close.
import React from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Printer, X, CheckCircle2 } from "lucide-react";

const BALI_TZ = "Asia/Makassar";

function fmtTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-GB", {
    hour: "2-digit", minute: "2-digit", timeZone: BALI_TZ, hour12: false,
  });
}

export default function ViewAppointmentDialog({
  open,
  onOpenChange,
  appt,
  branchName = "",
  roomName = "",
  staffName = "",
  onEdit,
  onTakePayment,
  onCheckIn,
  onPrint,
}) {
  if (!appt) return null;
  const start = fmtTime(appt.start_iso);
  const end = fmtTime(appt.end_iso);
  const svcMin = appt.service_time_min
    || (Number(appt.duration_min || 0) - Number(appt.processing_time_min || 0))
    || appt.duration_min
    || 0;
  // Iter-270 · Take-Payment gate mirrors ContextMenu.jsx exactly — the
  // button is Disabled until the guest has been physically Checked-In
  // (backend stamps `checked_in_at` when the QR / front-desk flow
  // completes). The live-sync `appointment_updated` broadcast is
  // consumed by BranchCalendar and the `viewAppt` prop is remapped to
  // the freshest appointment doc on every re-render, so this unlocks
  // instantly WITHOUT closing / re-opening the dialog.
  const isCheckedIn = !!appt.checked_in_at;
  const isCancelled = appt.status === "cancelled" || appt.status === "no_show";
  const canTakePayment = isCheckedIn && !isCancelled;
  const payDisabledTitle = "Client must be Checked-In before final payment can be taken.";
  const deposit = Number(appt.deposit_amount || 0);
  const balance = Number(appt.deposit_balance_due || 0);
  const notes = (appt.notes || "").trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="p-0 max-w-md gap-0 rounded-md overflow-hidden bg-white border-0 shadow-xl"
        data-testid="view-appt-dialog"
      >
        {/* Header — dark burgundy strip to echo the reference visual */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-[#6b2b2f] text-white">
          <h2 className="text-sm font-medium tracking-wide" data-testid="view-appt-title">View Appointment</h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onPrint}
              className="p-1 rounded hover:bg-white/10 transition"
              title="Print receipt"
              data-testid="view-appt-print"
            >
              <Printer className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="p-1 rounded hover:bg-white/10 transition text-rose-200 hover:text-rose-100"
              title="Close"
              data-testid="view-appt-close-icon"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-3 text-sm">
          <div className="flex items-start gap-3">
            <div className="text-xs text-muted-foreground w-16 shrink-0 tabular-nums" data-testid="view-appt-time">
              {start} – {end}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-foreground" data-testid="view-appt-service">
                {appt.service_name} <span className="font-normal text-muted-foreground">({svcMin} minutes)</span>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5" data-testid="view-appt-location">
                {[branchName, staffName, roomName].filter(Boolean).join(" · ")}
              </div>
            </div>
          </div>

          <div className="pt-1 space-y-1 text-[13px]">
            <div className="flex gap-2">
              <span className="text-muted-foreground w-14 text-right shrink-0">Name:</span>
              {appt.customer_id ? (
                <Link
                  to={`/staff/brand/customers/${appt.customer_id}`}
                  className="text-rose-700 hover:underline font-medium"
                  data-testid="view-appt-customer-link"
                  onClick={() => onOpenChange(false)}
                >
                  {appt.customer_name}
                </Link>
              ) : (
                <span className="font-medium">{appt.customer_name || "Walk-in"}</span>
              )}
            </div>
            <div className="flex gap-2">
              <span className="text-muted-foreground w-14 text-right shrink-0">Phone:</span>
              <span data-testid="view-appt-phone">{appt.customer_phone || appt.customer_whatsapp || "—"}</span>
            </div>
            <div className="flex gap-2">
              <span className="text-muted-foreground w-14 text-right shrink-0">Email:</span>
              <span data-testid="view-appt-email" className="truncate">{appt.customer_email || "—"}</span>
            </div>
            {notes && (
              <div className="flex gap-2">
                <span className="text-muted-foreground w-14 text-right shrink-0">Notes:</span>
                <span className="italic text-muted-foreground" data-testid="view-appt-notes">{notes}</span>
              </div>
            )}
          </div>

          {(deposit > 0 || balance > 0) && (
            <div
              className="mt-2 rounded border border-border/60 bg-muted/40 px-3 py-2 text-[11px] text-muted-foreground"
              data-testid="view-appt-deposit-strip"
            >
              Deposit paid: <span className="font-medium text-foreground tabular-nums">{deposit.toLocaleString()}</span>
              {" · "}Balance due: <span className="font-medium text-foreground tabular-nums">{balance.toLocaleString()}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-border/60 bg-muted/30">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
            data-testid="view-appt-close-link"
          >
            « Close
          </button>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              onClick={onEdit}
              className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3"
              data-testid="view-appt-edit"
            >
              Edit
            </Button>
            {isCheckedIn ? (
              <span
                className="inline-flex items-center gap-1 h-8 px-3 rounded-md bg-teal-50 text-teal-700 text-xs font-medium border border-teal-200"
                data-testid="view-appt-checked-in-pill"
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> Checked-in
              </span>
            ) : (
              <Button
                type="button"
                size="sm"
                onClick={onCheckIn}
                variant="outline"
                className="h-8 px-3 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="view-appt-checkin"
                disabled={isCancelled}
              >
                Check-in
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              onClick={canTakePayment ? onTakePayment : undefined}
              disabled={!canTakePayment}
              aria-disabled={!canTakePayment}
              className={
                "h-8 px-3 text-white " +
                (canTakePayment
                  ? "bg-slate-500 hover:bg-slate-600"
                  : "bg-slate-300 hover:bg-slate-300 opacity-60 cursor-not-allowed")
              }
              title={canTakePayment ? "" : payDisabledTitle}
              data-testid="view-appt-take-payment"
              data-checked-in={isCheckedIn ? "true" : "false"}
            >
              Take Payment
            </Button>
          </div>
        </div>
        {!canTakePayment && !isCancelled && (
          <div
            className="px-4 pb-3 -mt-2 text-[11px] text-muted-foreground italic text-right"
            data-testid="view-appt-take-payment-hint"
          >
            {payDisabledTitle}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
